// Bulk import: drop the whole "posters" folder (sub-folder = category) and every
// picture becomes a product with its WebP poster + a "poster on the wall" photo.
import { decodeToDrawable, compressDrawable, uploadBlobAt, createProduct, fetchProducts, isSupportedImage } from './api';
import { makeMockup } from './mockup';
import { defaultSizesFor } from './categories';

// folder name (lowercase) -> { category on the site, label used in product names }
const FOLDERS = {
  anime: { category: 'Anime', label: 'Anime' },
  bikes: { category: 'Bikes', label: 'Bikes' },
  cars: { category: 'Cars', label: 'Cars' },
  cricketers: { category: 'Athletes', label: 'Cricketers' },
  f1: { category: 'Cars', label: 'F1' },
  'fighter jets': { category: 'Fighter Jets', label: 'Fighter Jet' },
  football: { category: 'Athletes', label: 'Football' },
  girly: { category: 'Girly', label: 'Girly' },
  gods: { category: 'Gods', label: 'Gods' },
  'laptop stickers': { category: 'Laptop Stickers', label: 'Laptop Stickers', laptop: true },
  'martial arts': { category: 'Athletes', label: 'Martial Arts' },
  motivation: { category: 'Motivational', label: 'Motivational' },
  motivational: { category: 'Motivational', label: 'Motivational' },
  music: { category: 'Music', label: 'Music' },
  others: { category: 'Aesthetic', label: 'Aesthetic' },
  aesthetic: { category: 'Aesthetic', label: 'Aesthetic' },
  athletes: { category: 'Athletes', label: 'Athletes' },
  asthestic: { category: 'Aesthetic', label: 'Aesthetic' },
  aesthetics: { category: 'Aesthetic', label: 'Aesthetic' },
  'pink edition': { category: 'Girly', label: 'Pink Edition' },
  'split posters': { category: 'Split Posters', label: 'Split' },
};

export const folderInfo = (name) => FOLDERS[String(name || '').trim().toLowerCase()] || null;

// ---- reading a dropped folder -------------------------------------------------
const readAllEntries = (reader) =>
  new Promise((resolve, reject) => {
    const all = [];
    const next = () =>
      reader.readEntries((batch) => {
        if (!batch.length) resolve(all);
        else {
          all.push(...batch);
          next();
        }
      }, reject);
    next();
  });

const entryFile = (entry) => new Promise((resolve, reject) => entry.file(resolve, reject));

async function walk(entry, parent, out) {
  if (entry.isFile) {
    out.push({ file: await entryFile(entry), folder: parent });
  } else if (entry.isDirectory) {
    const kids = await readAllEntries(entry.createReader());
    for (const k of kids) {
      // eslint-disable-next-line no-await-in-loop
      await walk(k, entry.name, out);
    }
  }
}

// from a drop event
export async function collectFromDrop(dataTransfer) {
  const out = [];
  const entries = Array.from(dataTransfer.items || [])
    .map((it) => (it.webkitGetAsEntry ? it.webkitGetAsEntry() : null))
    .filter(Boolean);
  for (const e of entries) {
    // eslint-disable-next-line no-await-in-loop
    await walk(e, e.isDirectory ? e.name : '', out);
  }
  return out;
}

// from <input type="file" webkitdirectory>
export function collectFromInput(fileList) {
  return Array.from(fileList || []).map((file) => {
    const parts = (file.webkitRelativePath || '').split('/');
    return { file, folder: parts.length >= 2 ? parts[parts.length - 2] : '' };
  });
}

// ---- helpers --------------------------------------------------------------------
async function fileHash(file) {
  const buf = await file.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .slice(0, 8)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

const pad = (n) => String(n).padStart(2, '0');

/**
 * Plan what will happen (no uploading yet).
 * Returns { items, ignored } where items are image files with a known folder.
 */
export function planImport(found) {
  const items = [];
  const ignored = [];
  for (const f of found) {
    const info = folderInfo(f.folder);
    if (!isSupportedImage(f.file)) continue; // thumbs.db etc. — silently skip
    if (!info) {
      ignored.push(f);
      continue;
    }
    items.push({ ...f, info });
  }
  items.sort((a, b) =>
    a.info.label === b.info.label
      ? a.file.name.localeCompare(b.file.name, undefined, { numeric: true })
      : a.info.label.localeCompare(b.info.label)
  );
  return { items, ignored };
}

/**
 * Run the import. onProgress({done,total,current,added,skipped,failed,log})
 * control.cancelled = true stops after the current poster.
 */
export async function runImport(items, onProgress, control) {
  const existing = await fetchProducts();
  const knownHashes = new Set();
  const counters = {};
  existing.forEach((p) => {
    (p.images || [p.image_url]).forEach((u) => {
      const m = /products\/([0-9a-f]{16})/.exec(u || '');
      if (m) knownHashes.add(m[1]);
    });
  });
  const stat = { done: 0, total: items.length, added: 0, skipped: 0, failed: 0, current: '', log: [] };
  const push = () => onProgress({ ...stat, log: [...stat.log] });

  for (const it of items) {
    if (control.cancelled) break;
    const { file, info } = it;
    stat.current = `${info.label} / ${file.name}`;
    push();
    try {
      const hash = await fileHash(file);
      if (knownHashes.has(hash)) {
        stat.skipped += 1;
        stat.log.push(`Skipped (already on site or duplicate): ${info.label} / ${file.name}`);
      } else {
        const src = await decodeToDrawable(file);
        const poster = await compressDrawable(src);
        const wall = await makeMockup(src, { key: hash, laptop: !!info.laptop });
        const posterUrl = await uploadBlobAt(`products/${hash}.${poster.ext}`, poster.blob);
        const wallUrl = await uploadBlobAt(`products/${hash}-wall.${wall.ext}`, wall.blob);

        const prefix = info.laptop ? 'Laptop Sticker Sheet' : `${info.label} Poster`;
        if (counters[prefix] == null) {
          counters[prefix] = existing.filter((p) => p.name.startsWith(`${prefix} `)).length;
        }
        counters[prefix] += 1;
        await createProduct({
          name: `${prefix} ${pad(counters[prefix])}`,
          description: info.laptop
            ? 'Stick-on laptop sticker sheet. Easy to peel and place.'
            : `Premium ${info.label.toLowerCase()} wall poster. Available in A4 and A3.`,
          category: info.category,
          image_url: posterUrl,
          image_width: poster.width,
          image_height: poster.height,
          images: [posterUrl, wallUrl],
          sizes: defaultSizesFor(info.category),
        });
        knownHashes.add(hash);
        stat.added += 1;
      }
    } catch (e) {
      stat.failed += 1;
      stat.log.push(`FAILED: ${info.label} / ${file.name} — ${e.message || e}`);
    }
    stat.done += 1;
    push();
  }
  stat.current = '';
  stat.finished = true;
  stat.cancelled = !!control.cancelled;
  push();
}
